import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchPhoneNumberPrice,
  fetchPhoneNumberPrices,
  updatePhoneNumberPrice
} from "../../actions/callrecorder/phoneNumberPricesActions";

export interface PhoneNumberPriceInterface {
  url: string;

  id: number;
  provider_id: number;
  country_code: string;
  phone_number_type: string;
  currency: string;
  prefix: string;

  monthly_fee: string;
  setup_fee: string;

  sms_enabled: boolean;
  mms_enabled: boolean;
  trunking_enabled: boolean;
  address_required: boolean;
  beta: boolean;
  domestic_voice_only: boolean;
  domestic_sms_only: boolean;

  raw_monthly_fee: string | null;
  raw_inbound_trunking_price: string | null;
  raw_inbound_trunking_from_landline: string | null;
  raw_inbound_trunking_from_mobile: string | null;
  raw_inbound_sms_price: string | null;
  raw_inbound_mms_price: string | null;

  inbound_cost_per_min: string | null;
  inbound_sms_price: string | null;
  inbound_mms_price: string | null;

  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,
};

const adaptor = createEntityAdapter<PhoneNumberPriceInterface>();

const slice = createSlice({
  name: 'phoneNumberPrices',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchPhoneNumberPrices.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchPhoneNumberPrices.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.phoneNumberPrices;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchPhoneNumberPrices.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchPhoneNumberPrice.fulfilled, (state, action) => {
        const price = Object.values(action.payload.phone_number_price)[0];
        state.entities[price.id] = price;
      })

      // #update
      .addCase(updatePhoneNumberPrice.fulfilled, (state, action) => {
        const price = Object.values(action.payload.phone_number_price)[0];
        state.entities[price.id] = price;
      })
      .addCase(updatePhoneNumberPrice.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
} = slice.actions;

export const {
  selectById: selectPhoneNumberPriceById,
  selectIds: selectPhoneNumberPriceIds,
  selectEntities: selectPhoneNumberPriceEntities,
} = adaptor.getSelectors<RootState>(state => state.callrecorder.phoneNumberPrices);
